/**
 * What: Presents the single account sign-in action for an unconnected Shopify store.
 * Transport / data-flow: Pure rendering of the page lifecycle's action state; callbacks own navigation/reload.
 * Invariants: No availability, waitlist or membership claims; failure recovery does not fake a lifecycle reset.
 */
import { useState, type ReactElement } from 'react'
import { launchAdmissionCopy as copy, shopifyComponentCopy } from '@testfeed/product-language'
import { ActionButton, type ActionButtonLifecycle } from './ActionButton'

/** Renders the production card, also used by Ladle without starting authentication or a store read. */
export function TestFeedSignInCard({
  shop,
  lifecycle,
  failed = false,
  onSignIn,
  onReload,
}: {
  shop: string
  lifecycle: ActionButtonLifecycle<'connect'>
  failed?: boolean
  onSignIn: () => unknown
  onReload: () => void
}): ReactElement {
  const [open, setOpen] = useState<'connect' | 'plan' | 'sync' | null>('connect')
  const home = shopifyComponentCopy.appHome
  let feedback = null
  let action = (
    <ActionButton lifecycle={lifecycle} action="connect" variant="primary" onClick={onSignIn}>
      {copy.signIn}
    </ActionButton>
  )
  if (failed) {
    feedback = <s-banner tone="critical" heading={copy.failed} />
    action = <s-button onClick={onReload}>{copy.reload}</s-button>
  }
  return (
    <s-section heading={copy.section}>
      <s-grid gap="small">
        <s-paragraph color="subdued">{copy.progress}</s-paragraph>
        <s-box borderRadius="base" border="base" background="base">
          <s-clickable
            padding="small"
            onClick={() => setOpen(open === 'connect' ? null : 'connect')}
            accessibilityLabel={home.stepConnectTitle}
          >
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
              <s-icon type="circle-dashed" tone="neutral"></s-icon>
              <s-text type="strong">{home.stepConnectTitle}</s-text>
              <s-icon type={open === 'connect' ? 'chevron-up' : 'chevron-down'} tone="neutral"></s-icon>
            </s-grid>
          </s-clickable>
          {open === 'connect' && (
            <s-box padding="small" paddingBlockStart="none">
              <s-box padding="base" background="subdued" borderRadius="base">
                <s-grid gap="small-200">
                  <s-text color="subdued">{shop}</s-text>
                  <s-paragraph>{copy.description}</s-paragraph>
                  {feedback}
                  <s-stack direction="inline">{action}</s-stack>
                </s-grid>
              </s-box>
            </s-box>
          )}
          <s-divider></s-divider>
          <s-clickable
            padding="small"
            onClick={() => setOpen(open === 'plan' ? null : 'plan')}
            accessibilityLabel={home.stepPlanTitle}
          >
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
              <s-icon type="lock" tone="neutral"></s-icon>
              <s-text>{home.stepPlanTitle}</s-text>
              <s-icon type={open === 'plan' ? 'chevron-up' : 'chevron-down'} tone="neutral"></s-icon>
            </s-grid>
          </s-clickable>
          {open === 'plan' && (
            <s-box padding="small">
              <s-paragraph>{copy.planLocked}</s-paragraph>
            </s-box>
          )}
          <s-divider></s-divider>
          <s-clickable
            padding="small"
            onClick={() => setOpen(open === 'sync' ? null : 'sync')}
            accessibilityLabel={home.stepSyncTitle}
          >
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
              <s-icon type="lock" tone="neutral"></s-icon>
              <s-text>{home.stepSyncTitle}</s-text>
              <s-icon type={open === 'sync' ? 'chevron-up' : 'chevron-down'} tone="neutral"></s-icon>
            </s-grid>
          </s-clickable>
          {open === 'sync' && (
            <s-box padding="small">
              <s-paragraph>{copy.syncLocked}</s-paragraph>
            </s-box>
          )}
        </s-box>
      </s-grid>
    </s-section>
  )
}
